import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudOff } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { removeWindowLocaleStorage } from '@/utils/windowSessionStorage'

/**
 * When Supabase HTTP returns 502/503/504 (see `lib/supabase.ts` global fetch), end the session
 * and send the user to the login page with a maintenance query flag.
 */
export function ServiceDownListener() {
    const navigate = useNavigate()
    const handlingRef = React.useRef(false)
    const [showOverlay, setShowOverlay] = React.useState(false)

    React.useEffect(() => {
        const onServerUnavailable = async () => {
            if (handlingRef.current) return
            handlingRef.current = true
            setShowOverlay(true)
            try {
                await supabase.auth.signOut()
            } catch {
                /* ignore */
            }
            try {
                sessionStorage.removeItem('staff_session')
                removeWindowLocaleStorage('staff_session')
                localStorage.removeItem('staff_session_shared')
                sessionStorage.removeItem('impersonate_tenant')
                localStorage.removeItem('impersonate_tenant')
                sessionStorage.removeItem('impersonate_tenant_name')
                localStorage.removeItem('impersonate_tenant_name')
            } catch {
                /* ignore */
            }
            try {
                sessionStorage.setItem('maintenance_logout', '1')
            } catch {
                /* ignore */
            }
            navigate('/login?maintenance=1', { replace: true })
        }
        window.addEventListener('app-server-unavailable', onServerUnavailable)
        return () => window.removeEventListener('app-server-unavailable', onServerUnavailable)
    }, [navigate])

    if (!showOverlay) return null

    return (
        <div
            className="fixed inset-0 z-[200000] flex items-center justify-center p-6 bg-slate-950/75 backdrop-blur-md"
            role="alert"
            aria-live="assertive"
        >
            <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-gradient-to-b from-slate-900/95 to-slate-950/95 p-10 text-center shadow-[0_32px_80px_-16px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(255,255,255,0.06)] ring-1 ring-white/5">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/[0.07] ring-1 ring-white/10">
                    <CloudOff className="text-slate-100" size={32} strokeWidth={1.75} />
                </div>
                <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-300">Service unavailable</p>
                <h2 className="mt-2 text-lg font-bold tracking-tight text-white">The platform is under maintenance</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    We could not reach the servers. Your session has been closed for your security. You will be redirected to sign in.
                </p>
                <div className="mt-8 flex justify-center">
                    <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
                </div>
            </div>
        </div>
    )
}
